import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, ChevronLeft, ChevronRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { WordCard } from "@/components/WordCard";
import { useProgress } from "@/hooks/useProgress";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 24;

export default function GalleryPage() {
  const { category: urlCategory } = useParams();
  const navigate = useNavigate();
  const [words, setWords] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState(urlCategory || "all");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const { markAsLearned, isLearned } = useProgress();
  const { user, syncProgress } = useAuth();
  const debounceRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/categories`).then((res) => setCategories(res.data)).catch(console.error);
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(0);
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [search]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ limit: PAGE_SIZE, skip: page * PAGE_SIZE });
        const countParams = new URLSearchParams();
        if (activeCategory !== "all") {
          params.set("category", activeCategory);
          countParams.set("category", activeCategory);
        }
        if (debouncedSearch.trim()) {
          params.set("search", debouncedSearch.trim());
          countParams.set("search", debouncedSearch.trim());
        }
        const [wRes, cntRes] = await Promise.all([
          axios.get(`${API}/words?${params}`),
          axios.get(`${API}/words/count?${countParams}`),
        ]);
        setWords(wRes.data);
        setTotalCount(cntRes.data.count);
      } catch (e) {
        console.error("Failed to load", e);
      }
      setLoading(false);
    };
    load();
  }, [activeCategory, page, debouncedSearch]);

  useEffect(() => {
    if (urlCategory) setActiveCategory(urlCategory);
  }, [urlCategory]);

  const handleCategoryChange = (slug) => {
    setActiveCategory(slug);
    setPage(0);
    setSearch("");
    setDebouncedSearch("");
    if (slug === "all") navigate("/gallery");
    else navigate(`/gallery/${slug}`);
  };

  const handleLearn = (wordId) => {
    markAsLearned(wordId);
    if (user) syncProgress(wordId);
    toast.success("Word marked as learned!");
  };

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  const paged = words;

  return (
    <div className="pt-24 pb-16 min-h-screen" data-testid="gallery-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <p className="text-sm uppercase tracking-[0.2em] text-nordic-text-muted font-semibold mb-3">Gallery</p>
          <h1 className="font-heading text-4xl md:text-5xl font-medium text-nordic-text tracking-tight mb-4">
            Explore Swedish Words
          </h1>
          <p className="text-nordic-text-secondary text-base md:text-lg max-w-xl mb-8">
            Browse our collection of {categories.reduce((a, c) => a + c.word_count, 0).toLocaleString()}+ Swedish words.
          </p>
        </motion.div>

        {/* Search */}
        <div className="relative max-w-md mb-6" data-testid="gallery-search">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-nordic-text-muted" />
          <Input
            placeholder="Search words..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-nordic-paper border-nordic-wood/30 focus:border-nordic-blue rounded-xl"
            data-testid="search-input"
          />
        </div>

        {/* Category scrollable bar */}
        <div className="mb-8 -mx-4 px-4 overflow-x-auto" data-testid="gallery-filters">
          <div className="flex gap-2 pb-2 min-w-max">
            <button
              onClick={() => handleCategoryChange("all")}
              className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap ${
                activeCategory === "all"
                  ? "bg-nordic-blue text-white"
                  : "bg-nordic-paper border border-nordic-wood/30 text-nordic-text-secondary hover:bg-nordic-wood/20"
              }`}
              style={{ transition: "background-color 0.2s ease, color 0.2s ease" }}
              data-testid="filter-all"
            >
              All
            </button>
            {categories.map((cat) => (
              <button
                key={cat.slug}
                onClick={() => handleCategoryChange(cat.slug)}
                className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap ${
                  activeCategory === cat.slug
                    ? "bg-nordic-blue text-white"
                    : "bg-nordic-paper border border-nordic-wood/30 text-nordic-text-secondary hover:bg-nordic-wood/20"
                }`}
                style={{ transition: "background-color 0.2s ease, color 0.2s ease" }}
                data-testid={`filter-${cat.slug}`}
              >
                {cat.name} <span className="text-xs opacity-70 ml-1">({cat.word_count})</span>
              </button>
            ))}
          </div>
        </div>

        {/* Results count */}
        <p className="text-sm text-nordic-text-muted mb-6" data-testid="results-count">
          {totalCount.toLocaleString()} {totalCount === 1 ? "word" : "words"} found
          {totalPages > 1 && ` — page ${page + 1} of ${totalPages}`}
        </p>

        {/* Word Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="rounded-2xl bg-nordic-paper/50 animate-pulse h-72" />
            ))}
          </div>
        ) : paged.length === 0 ? (
          <div className="text-center py-20" data-testid="no-results">
            <p className="text-nordic-text-muted text-lg">No words found.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="word-grid">
            {paged.map((word, i) => (
              <WordCard key={word.id} word={word} index={i} onLearn={handleLearn} isLearned={isLearned(word.id)} showDetails />
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-4 mt-10" data-testid="pagination">
            <Button
              variant="outline"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="rounded-full border-nordic-wood/30"
              data-testid="prev-page-btn"
            >
              <ChevronLeft className="w-4 h-4 mr-1" /> Previous
            </Button>
            <span className="text-sm text-nordic-text-muted">
              {page + 1} / {totalPages}
            </span>
            <Button
              variant="outline"
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="rounded-full border-nordic-wood/30"
              data-testid="next-page-btn"
            >
              Next <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
